import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabase } from '../../components/supabaseClient';
import {
  isCutoverEffective, startPriorVisitsModalFromRds, servePriorVisitsModalFromRds,
} from '../../utils/rdsCutover';
import { bucketName_files } from '../../components/env';
import { buildDocPath } from '../../hooks/medstudent/usePatientDocument';
import { chartSchema, chartTable } from './chartSource';
import { isVisitCompleted } from './utils';

function formatVisitDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// Local-date ISO (YYYY-MM-DD). new Date().toISOString() is UTC and lands on
// the wrong day for users west of UTC after their local midnight, so we
// build the string from local components to match what the visit table
// stores (`date_` is a local-date column on notes-c).
function todayLocalIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function formatRelative(dateStr, today = new Date()) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 0 && diffDays < 7) return `${diffDays}d ago`;
  if (diffDays > 0 && diffDays < 30) return `${Math.round(diffDays / 7)}w ago`;
  if (diffDays > 0 && diffDays < 365) return `${Math.round(diffDays / 30)}mo ago`;
  if (diffDays > 0) return `${Math.round(diffDays / 365)}y ago`;
  if (diffDays === -1) return 'Tomorrow';
  return `in ${Math.abs(diffDays)}d`;
}

function formatTime(t) {
  if (!t) return '';
  if (typeof t !== 'string') return '';
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

// Pull plain text out of a Quill delta so the preview pane shows the note
// without rehydrating Quill. Mirrors MedStudentClinicPage.opsArrayToPlainText
// — embeds become ￼ so the visible string stays in sync with character
// positions in the source (good enough for a glanceable preview).
function opsToPlain(ops) {
  if (!Array.isArray(ops)) return '';
  return ops.map((op) => {
    if (typeof op.insert === 'string') return op.insert;
    if (op.insert != null) return '￼';
    return '';
  }).join('');
}

function StatusBadge({ row }) {
  // `signed_at` lives on document_signatures (one row per document), not on
  // notes-c, so we can't select it from the list query. Instead, surface
  // "Signed" only when the caller (MedStudentClinicPage) pre-attached a
  // `signed` flag to the row — otherwise fall back to isVisitCompleted.
  if (row.signed) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Signed
      </span>
    );
  }
  if (isVisitCompleted(row)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Completed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
      Active
    </span>
  );
}

export default function PriorVisitsModal({
  isOpen,
  onClose,
  patient,
  currentUuid,
  schema: schemaOverride = null,
  onOpenVisit, // (row) => void — optional override; defaults to navigate to clinic?date=
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const patientName = patient?.patient_name || '';
  const patientEhrId = patient?.EHR_ID || null;
  const isRoster = chartTable(patient) === 'patients_with_ehrid';
  const targetSchema = schemaOverride || chartSchema(patient);

  const currentDate = patient?.date_ || null;

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRows([]);
    setPreviewRow(null);
    setPreviewText('');
    setPreviewError(null);

    (async () => {
      try {
        const supabase = await getSupabase();
        // Roster charts have no date_ history to surface; the per-day doc
        // bucket is keyed by the roster row id, not a date.
        if (isRoster) {
          if (!cancelled) {
            setRows([]);
            setIsLoading(false);
          }
          return;
        }
        if (!patientName && !patientEhrId) {
          if (!cancelled) {
            setError('Patient has no name or EHR ID — cannot list prior visits.');
            setIsLoading(false);
          }
          return;
        }

        // Query by EHR_ID when present (most reliable cross-schema identifier)
// and fall back to patient_name. EHR_ID is preferred because the same
// patient under different schemas can be matched on the same id, but
// notes-c rows have historically been keyed by patient_name.
//
// Date filter: only past + today's visits — never future ones. The
// clinic's purpose here is to glance at last time's note while
// writing today's, so future appointments would just be noise
// (and a freshly-added "tomorrow" visit would crowd out the real
// history). `date_.lte.today` covers both "past" and "actual".
// The active visit is filtered out by id below so the user isn't
// staring at the row they already have open.
//
// Column list intentionally conservative: notes-c does NOT have
// signed_at (that lives on document_signatures, one row per
// document). Picking an unknown column blows the whole query up
// (PostgREST returns 400 with a column-doesn't-exist error), which
// is what made the modal render with 0 visits on file instead of
// showing the list. Stick to the fields we know are safe.
const todayIso = todayLocalIso();
// Phase 4 cutover, site 10 (utils/rdsCutover.js `prior_visits_modal`):
// RDS is asked the same question alongside the Supabase read; Supabase's
// rows stay the parity oracle and the fallback. Off = the old behaviour.
const modalCutover = await isCutoverEffective('prior_visits_modal');
if (cancelled) return;
const rdsStarted = modalCutover
  ? startPriorVisitsModalFromRds(targetSchema, { ehrId: patientEhrId, patientName, todayIso })
  : null;
let query = supabase
  .schema(targetSchema)
  .from('notes-c')
  .select('id, EHR_ID, patient_name, date_, time_, type, template, notes_behavior, transcripts, more_audios')
  // Future visits are hidden — the popup is for looking back, not
  // forward. Using local-date ISO so a UTC-vs-local edge doesn't
  // accidentally hide today's visit in negative-UTC zones.
  .lte('date_', todayIso);
if (patientEhrId) {
  query = query.eq('EHR_ID', patientEhrId);
} else {
  query = query.eq('patient_name', patientName);
}
const { data, error: qErr } = await query
  .order('date_', { ascending: false })
  .limit(200);
if (cancelled) return;
if (qErr) {
  setError(qErr.message || 'Failed to fetch prior visits.');
  setRows([]);
} else {
  // Drop the currently-open visit. With the lte filter above the
  // active row is already included (same date / earlier), but the
  // user doesn't need to see their own current row in the history
  // list — they're already viewing it.
  let rowsIn = Array.isArray(data) ? data : [];
  if (rdsStarted) {
    const served = await servePriorVisitsModalFromRds(targetSchema, {
      ehrId: patientEhrId, patientName, todayIso, supabaseRows: rowsIn, started: rdsStarted,
    });
    if (cancelled) return;
    rowsIn = served.rows;
  }
  const filtered = rowsIn.filter(
    (r) => r && r.id !== currentUuid,
  );
  setRows(filtered);
}
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to fetch prior visits.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, patientName, patientEhrId, currentUuid, currentDate, targetSchema, isRoster]);

  const filteredRows = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.trim().toLowerCase();
    return rows.filter((r) =>
      (r.patient_name || '').toLowerCase().includes(q)
      || (r.type || '').toLowerCase().includes(q)
      || (r.template || '').toLowerCase().includes(q)
      || (r.date_ || '').toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const handleOpen = useCallback((row) => {
    if (!row?.date_) return;
    if (onOpenVisit) {
      onOpenVisit(row);
      onClose();
      return;
    }
    // Each visit date has its own notes-c row with a unique `id`, and the
    // clinic page fetches the patient/document by THAT id. Just changing
    // `?date=` against the current uuid doesn't reload anything — the hook
    // still resolves to the same row, so the document stays put. Navigate
    // to the prior visit's own id and pass the schema explicitly so a
    // cross-schema patient doesn't bounce to home on the re-fetch.
    const visitId = row.id || currentUuid;
    const params = new URLSearchParams();
    if (targetSchema && targetSchema !== 'public') params.set('schema', targetSchema);
    const qs = params.toString();
    onClose();
    navigate(`/clinic/${visitId}${qs ? `?${qs}` : ''}`);
  }, [navigate, currentUuid, targetSchema, onOpenVisit, onClose]);

  const handlePreview = useCallback(async (row) => {
    if (!row) return;
    setPreviewRow(row);
    setPreviewText('');
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const supabase = await getSupabase();
      // Synthesize the row shape buildDocPath expects (it reads patient_name,
      // EHR_ID, id, date_). The fetched row already has those.
      const path = buildDocPath(row, targetSchema);
      if (!path) {
        setPreviewError('No document path for this visit.');
        setPreviewLoading(false);
        return;
      }
      const { data: blob, error: dlErr } = await supabase.storage
        .from(bucketName_files)
        .download(path);
      if (dlErr || !blob) {
        setPreviewError('No saved note for this visit.');
        setPreviewLoading(false);
        return;
      }
      const parsed = JSON.parse(await blob.text());
      const ops = parsed?.delta?.ops;
      setPreviewText(opsToPlain(ops) || '(empty note)');
    } catch (e) {
      setPreviewError(e?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }, [targetSchema]);

  // Close on Esc.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (previewRow) setPreviewRow(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, previewRow, onClose]);

  if (!isOpen) return null;

  // Render inline (NOT via createPortal). The clinic app's Tailwind config
  // sets `important: '#clinic-root'`, so every utility — including `fixed`,
  // `inset-0`, and the backdrop color — only applies inside that wrapper.
  // A portal to `document.body` would put the modal outside that scope, and
  // `position: fixed` + the dim backdrop would silently stop working (the
  // modal would just stack at the bottom of <body>). Inline keeps it under
  // #clinic-root so all utilities apply.
  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (previewRow) setPreviewRow(null);
          else onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
          <div className="flex flex-col min-w-0">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 truncate">
              Prior visits
              {patientName ? <span className="text-slate-500 dark:text-slate-400 font-normal"> · {patientName}</span> : null}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {rows.length} {rows.length === 1 ? 'visit' : 'visits'} on file
              {targetSchema && targetSchema !== 'public' ? ` · schema ${targetSchema}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by date, type…"
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 w-56"
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body: split into list (left) + preview pane (right) when a row is selected */}
        <div className="flex-1 flex min-h-0">
          {/* List */}
          <div className={`${previewRow ? 'w-2/5 border-r border-slate-200 dark:border-slate-700' : 'w-full'} overflow-y-auto`}>
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-slate-500 dark:text-slate-400">
                <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Loading prior visits…
              </div>
            ) : error ? (
              <div className="px-6 py-10 text-sm text-rose-600 dark:text-rose-400">{error}</div>
            ) : isRoster ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                Roster charts don't have a per-day visit history. Open the patient's profile to see their encounter timeline.
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                {rows.length === 0 ? 'No prior visits on file for this patient.' : 'No visits match your filter.'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredRows.map((row) => {
                  const isActive = previewRow?.id === row.id;
                  return (
                    <li
                      key={row.id}
                      className={`group px-5 py-3.5 transition-colors ${
                        isActive
                          ? 'bg-indigo-50/70 dark:bg-indigo-500/10'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => handlePreview(row)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {formatVisitDate(row.date_)}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {formatTime(row.time_)}
                            </span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">
                              {formatRelative(row.date_)}
                            </span>
                            <StatusBadge row={row} />
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            {(row.type || row.template) && (
                              <span className="font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                {row.type || row.template}
                              </span>
                            )}
                            {row.notes_behavior && row.notes_behavior !== 'fresh' && (
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                {row.notes_behavior}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handlePreview(row)}
                            className="px-2.5 py-1 text-[11px] font-semibold rounded-md text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/15 hover:bg-indigo-100 dark:hover:bg-indigo-500/25 transition-colors"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpen(row)}
                            className="px-2.5 py-1 text-[11px] font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400 transition-colors"
                          >
                            Open
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Preview pane */}
          {previewRow && (
            <div className="w-3/5 flex flex-col bg-slate-50 dark:bg-slate-900/60">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold">
                    Preview
                  </div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {formatVisitDate(previewRow.date_)} · {formatTime(previewRow.time_)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpen(previewRow)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    Open this visit
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewRow(null)}
                    aria-label="Close preview"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full text-sm text-slate-500 dark:text-slate-400">
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Loading note…
                  </div>
                ) : previewError ? (
                  <div className="flex items-center justify-center h-full text-sm text-amber-600 dark:text-amber-400">
                    {previewError}
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed text-slate-800 dark:text-slate-200">
                    {previewText}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-6 py-2.5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[10px] font-mono">Esc</kbd>{' '}
            to close
            {previewRow ? ' preview' : ''}
          </span>
          <span>Open switches the active chart; Preview loads the note here.</span>
        </div>
      </div>
    </div>
  );
}
